/**
 * Single source of truth for order/store status presentation.
 *
 * Keeps badges, dots, donut slices and table cells consistent across pages.
 * Tones map to existing design tokens so light/dark themes inherit correctly.
 */
import type { BadgeProps } from '@/components/ui/Badge';

export type OrderStatus =
  | 'created'
  | 'packer_assigned'
  | 'packed'
  | 'billing'
  | 'picked'
  | 'completed'
  | 'cancelled'
  // Defensive: legacy / WS payload variants we sometimes see.
  | 'in_progress'
  | 'pending'
  | string;

export type StatusTone = NonNullable<BadgeProps['tone']>;

interface StatusMeta {
  label: string;
  tone: StatusTone;
  /** Tailwind background color for a 6-8px dot. */
  dot: string;
  /** Sort order for left→right funnel charts; lower = earlier in the journey. */
  order: number;
  /** Subtle helper copy used in tooltips. */
  hint: string;
}

const FALLBACK: StatusMeta = {
  label: 'Unknown',
  tone: 'neutral',
  dot: 'bg-muted-foreground',
  order: 999,
  hint: 'Status not recognised — check the source.',
};

/**
 * The canonical order lifecycle. Order matters — funnel charts and SLA
 * heatmaps lean on this sort order. Tones follow taxonomy:
 *   info     – brand-neutral progress
 *   warning  – staff action required
 *   primary  – customer-facing/billing
 *   success  – terminal happy path
 *   danger   – terminal sad path
 */
const MAP: Record<string, StatusMeta> = {
  created: { label: 'Created', tone: 'info', dot: 'bg-info', order: 1, hint: 'Order placed; waiting for assignment.' },
  packer_assigned: { label: 'Assigned', tone: 'warning', dot: 'bg-warning', order: 2, hint: 'Packer assigned; ready to pack.' },
  packed: { label: 'Packed', tone: 'warning', dot: 'bg-warning', order: 3, hint: 'Packed; waiting for billing.' },
  billing: { label: 'Billing', tone: 'primary', dot: 'bg-primary', order: 4, hint: 'Bill being generated/printed.' },
  picked: { label: 'Picked up', tone: 'success', dot: 'bg-success', order: 5, hint: 'Rider picked up; in transit.' },
  completed: { label: 'Completed', tone: 'success', dot: 'bg-success', order: 6, hint: 'Delivered & closed.' },
  cancelled: { label: 'Cancelled', tone: 'danger', dot: 'bg-danger', order: 7, hint: 'Order cancelled.' },
  in_progress: { label: 'In progress', tone: 'info', dot: 'bg-info', order: 1, hint: 'Generic in-progress state.' },
  pending: { label: 'Pending', tone: 'info', dot: 'bg-info', order: 1, hint: 'Pending action.' },
};

export function statusMeta(status: OrderStatus): StatusMeta {
  return MAP[status] ?? { ...FALLBACK, label: humanize(String(status)) };
}

export function statusLabel(status: OrderStatus): string {
  return statusMeta(status).label;
}

export function statusTone(status: OrderStatus): StatusTone {
  return statusMeta(status).tone;
}

export function statusDot(status: OrderStatus): string {
  return statusMeta(status).dot;
}

export function statusOrder(status: OrderStatus): number {
  return statusMeta(status).order;
}

export function statusHint(status: OrderStatus): string {
  return statusMeta(status).hint;
}

/** Lifecycle without terminal states — used by Live Ops counters. */
export const ACTIVE_ORDER_STATUSES: OrderStatus[] = [
  'created',
  'packer_assigned',
  'packed',
  'billing',
  'picked',
];

export function isTerminal(status: OrderStatus): boolean {
  return status === 'completed' || status === 'cancelled';
}

function humanize(s: string): string {
  return s
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (m) => m.toUpperCase())
    .trim();
}
